import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { isToday } from 'date-fns'
import { baseUrl } from '../../utils/axios'
import { toast } from 'react-toastify'
import { handleErrors, formatParams, getParams, formatDateForBackend } from 'utils/helpers'
import { AxiosResponse } from 'axios'
import {
  IAccommodationGuestsPaginationResponse,
  IBedHistoryPaginationResponse,
  IPaginatedResponse,
} from 'types/api/general.interface'
import { IAccommodationModalCamp, IAccommodationSlice } from 'types/accommodation.interface'
import {
  IAccommodationInfoResponse,
  IAccommodationSingleResponse,
  ICheckInPostRequest,
  ICreateCampPostRequest,
  ICreateGuestPostRequest,
  ICreateGuestPostRequestResponse,
  IGetAccommodationStatistics,
  IGetAllAccommodationGuestsSingleResponse,
  IGetAllAccommodationsSingleResponse,
  IGetAllAccommodationUsersSingleResponse,
  IGetAllBedsSingleResponse,
  IGetAllBedsHiistorySingleResponse,
  IGetAllCampsSingleResponse,
  IGetAllExpectedCheckoutsSingleResponse,
} from 'types/api/accommodationApi.interface'
import { accommodationCategories, accommodationTypes } from 'pages/Accomodation/accommodationData'
import { IConfirmCheckInModal } from 'pages/Accomodation/ConfirmCheckInModal'

const initialState: IAccommodationSlice = {
  isLoading: false,
  statistics: null,
  accommodations: [],
  singleAccommodationInfo: null,
  totalCount: 0,
  totalPages: 0,
  guests: {
    data: [],
    totalCount: 0,
    totalPages: 0,
    overstayed: 0,
    expiringSoon: 0,
    active: 0,
    total: 0,
  },
  bedsHistory: {
    data: [],
    totalCount: 0,
    totalPages: 0,
    overdueCount: 0,
    completedCount: 0,
    activeCount: 0,
  },
  checkOuts: {
    data: [],
    totalCount: 0,
    totalPages: 0,
  },
  camps: {
    data: [],
    totalCount: 0,
    totalPages: 0,
  },
  beds: {
    data: [],
    totalCount: 0,
    totalPages: 0,
  },
  accomodationUsers: {
    data: [],
    totalCount: 0,
    totalPages: 0,
  },
}

export const extendStay = createAsyncThunk(
  'accommodations/extendStay',
  async (state: any, thunkAPI) => {
    try {
      const { id } = state
      delete state.id

      const resp: AxiosResponse<IPaginatedResponse<IGetAllAccommodationsSingleResponse>> =
        await baseUrl.patch('accommodation/management/accommodation/extend-stay/' + id, state)
      await thunkAPI.dispatch(getBedsHistory({ limit: 99, page: 1, personType: 2 }))

      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getAccommodations = createAsyncThunk(
  'accommodations/getAccommodations',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IPaginatedResponse<IGetAllAccommodationsSingleResponse>> =
        await baseUrl.get('accommodation/management/accommodation', { params })
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getAccommodationGuests = createAsyncThunk(
  'accommodations/getAccommodationGuests',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<
        IAccommodationGuestsPaginationResponse<IGetAllAccommodationGuestsSingleResponse>
      > = await baseUrl.get('accommodation/management/accommodation/guests', { params })
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getAccommodationUsers = createAsyncThunk(
  'accommodations/getAccommodationUsers',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IPaginatedResponse<IGetAllAccommodationUsersSingleResponse>> =
        await baseUrl.get('accommodation/management/accommodation/users', { params })
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getSingleAccommodationInfo = createAsyncThunk(
  'accommodations/getSingleAccommodationInfo',
  async (id: string, thunkAPI) => {
    try {
      const resp: AxiosResponse<IAccommodationInfoResponse> = await baseUrl.get(
        'accommodation/management/accommodation/info/' + id,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getCheckouts = createAsyncThunk(
  'accommodations/getCheckouts',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IGetAllExpectedCheckoutsSingleResponse> = await baseUrl.get(
        'accommodation/management/accommodation/expected-checkout-by-date',
        { params },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const checkIn = createAsyncThunk(
  'accommodations/checkIn',
  async (state: IConfirmCheckInModal, thunkAPI) => {
    try {
      let guestResp: AxiosResponse<ICreateGuestPostRequestResponse> = null
      if (state.type === 'Guest' && !state.complance) {
        const guestObj: ICreateGuestPostRequest = {
          identificationNumber: state.identificationNumber,
          firstName: state.person.split(' ')[0],
          lastName: state.person.split(' ')[1] || '',
          gender: state.gender?.value == 1 ? 'm' : 'f',
          expectedCheckoutDate: formatDateForBackend(state.checkOut),
        }
        guestResp = await baseUrl.post('accommodation/management/accommodation/guest', guestObj)
      }

      const checkInDate = state.checkIn ? new Date(state.checkIn) : new Date()
      const checkInValue = isToday(checkInDate) ? new Date() : checkInDate

      const obj: ICheckInPostRequest = {
        userId: state.type === 'Employee' ? state.userId : null,
        accommodationGuestId: state.type === 'Guest' ? guestResp.data.id : null,
        personType: state.type === 'Employee' ? 1 : 2,
        bedId: state.bedId,
        checkInTimestamp: formatDateForBackend(checkInValue),
        expectedCheckOutTimestamp: formatDateForBackend(state.checkOut),
        overrideReason: '',
      }
      const resp = await baseUrl.post('accommodation/management/accommodation/check-in', obj)
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)
export const checkOut = createAsyncThunk(
  'accommodations/checkOut',
  async (id: number, thunkAPI) => {
    try {
      await baseUrl.post(`accommodation/management/accommodation/check-out/${id}`)
      await thunkAPI.dispatch(getCheckouts(getParams({ location })))
      return id
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getSingleAccommodation = createAsyncThunk(
  'accommodations/getSingleAccomodation',
  async (id: string, thunkAPI) => {
    try {
      const resp: AxiosResponse<IAccommodationSingleResponse> = await baseUrl.get(
        'accommodation/management/accommodation/' + id,
      )
      const state = {
        id: resp.data.id,
        name: resp.data.name,
        address: resp.data.address,
        category: accommodationCategories.find((c) => c.value === resp.data.category),
        type: accommodationTypes.find((t) => t.value === resp.data.type),
        startDate: new Date(resp.data.startDate),
        endDate: new Date(resp.data.endDate),
        rooms: resp.data.rooms,
      }
      return state
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const createCamp = createAsyncThunk(
  'accommodations/createCamp',
  async (state: IAccommodationModalCamp, thunkAPI) => {
    const req: ICreateCampPostRequest = {
      name: state.name,
      address: state.address,
      coordinates: {
        latitude: state.coordinates.latitude || 0,
        longitude: state.coordinates.longitude || 0,
      },
      status: state.status?.value,
      blocks: state.blocks.map((block) => ({
        name: block.name,
        status: block.status?.value,
        genderPolicy: block.genderPolicy?.value,
        tierPolicy: block.tierPolicy?.value,
        rooms: block.rooms.map((room) => ({
          label: room.name,
          capacity: room.capacity,
          status: room.status?.value,
          beds: room.beds.map((bed) => ({
            label: bed.name,
            state: bed.state?.value,
          })),
        })),
      })),
    }
    try {
      const resp: AxiosResponse = await baseUrl.post(
        'accommodation/management/accommodation/camp',
        req,
      )
      await thunkAPI.dispatch(getCamps(getParams({ location })))
      await thunkAPI.dispatch(getAccommodationStatistics(getParams({ location })))
      return resp.data
    } catch (error) {
      console.log(error)
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const updateCamp = createAsyncThunk(
  'accommodations/updateCamp',
  async (state: IAccommodationModalCamp, thunkAPI) => {
    // Temp ids created in the modal are Date.now() timestamps (>= 1e12); real backend ids are small.
    const realId = (id?: number) => (id && id < 1e12 ? id : undefined)
    const req = {
      name: state.name,
      address: state.address,
      coordinates: {
        latitude: state.coordinates.latitude || 0,
        longitude: state.coordinates.longitude || 0,
      },
      status: state.status?.value,
      blocks: state.blocks.map((block) => ({
        id: realId(block.id),
        name: block.name,
        status: block.status?.value,
        genderPolicy: block.genderPolicy?.value,
        tierPolicy: block.tierPolicy?.value,
        rooms: block.rooms.map((room) => ({
          id: realId(room.id),
          label: room.name,
          capacity: room.capacity,
          status: room.status?.value,
          beds: room.beds.map((bed) => ({
            id: realId(bed.id),
            label: bed.name,
            state: bed.state?.value,
          })),
        })),
      })),
    }
    try {
      const resp: AxiosResponse = await baseUrl.patch(
        'accommodation/management/accommodation/camp/' + state.id,
        req,
      )
      await thunkAPI.dispatch(getCamps(getParams({ location })))
      await thunkAPI.dispatch(getAccommodationStatistics(getParams({ location })))
      return resp.data
    } catch (error) {
      console.log(error)
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getAccommodationStatistics = createAsyncThunk(
  'accommodations/getAccommodationStatistics',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IGetAccommodationStatistics> = await baseUrl.get(
        'accommodation/management/accommodation/statistics',
        { params },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getCamps = createAsyncThunk(
  'accommodations/getCamps',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IPaginatedResponse<IGetAllCampsSingleResponse>> = await baseUrl.get(
        'accommodation/management/accommodation/camps',
        { params },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getBeds = createAsyncThunk('accommodations/getBeds', async (params: any, thunkAPI) => {
  try {
    params = formatParams(params)
    const resp: AxiosResponse<IPaginatedResponse<IGetAllBedsSingleResponse>> = await baseUrl.get(
      'accommodation/management/accommodation/beds',
      { params },
    )
    return { ...resp.data, page: params?.page ?? 1 }
  } catch (error) {
    return thunkAPI.rejectWithValue(error)
  }
})

export const getBedsHistory = createAsyncThunk(
  'accommodations/getBedsHistory',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IPaginatedResponse<IGetAllBedsHiistorySingleResponse>> =
        await baseUrl.get('accommodation/management/accommodation/occupancies', { params })
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getCampsWithOccupancies = createAsyncThunk(
  'accommodations/getCampsWithOccupancies',
  async (params: any = {}, thunkAPI) => {
    const campsParams = params.campsParams ?? params
    const occupanciesParams = params.occupanciesParams ?? { page: 1, limit: 999 }

    const campsResp = await thunkAPI.dispatch(getCamps(campsParams))
    if (campsResp.meta.requestStatus !== 'fulfilled') {
      return thunkAPI.rejectWithValue(campsResp.payload)
    }

    const camps =
      (campsResp.payload as IPaginatedResponse<IGetAllCampsSingleResponse>).data ?? []

    if (camps.length > 0) {
      await thunkAPI.dispatch(getBedsHistory(occupanciesParams))
    } else {
      thunkAPI.dispatch(clearBedsHistory())
    }

    return campsResp.payload
  },
)

export const editAccommodation = createAsyncThunk(
  'accommodations/editAccomodation',
  async (state, thunkAPI) => {
    const req = {}
    try {
      const resp: AxiosResponse<IGetAllAccommodationsSingleResponse> = await baseUrl.patch(
        'accommodation/management/accommodation/' + state,
        req,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const deleteAccommodation = createAsyncThunk(
  'accommodations/deleteAccomodation',
  async (id: number, thunkAPI) => {
    try {
      await baseUrl.delete('accommodation/management/accommodation/' + id)
      await thunkAPI.dispatch(getAccommodations(getParams({ location })))
      return id
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const markBedClean = createAsyncThunk(
  'accommodations/markBedClean',
  async (id: number, thunkAPI) => {
    try {
      await baseUrl.patch(`accommodation/management/accommodation/bed/${id}/mark-clean`)
      return id
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const markRoomClean = createAsyncThunk(
  'accommodations/markRoomClean',
  async (id: number, thunkAPI) => {
    try {
      await baseUrl.patch(`accommodation/management/accommodation/room/${id}/mark-clean`)
      return id
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const blockRoom = createAsyncThunk(
  'accommodations/blockRoom',
  async (id: number, thunkAPI) => {
    try {
      await baseUrl.patch(`accommodation/management/accommodation/room/${id}/block`)
      return id
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

const accommodationSlice = createSlice({
  name: 'accommodation',
  initialState,
  reducers: {
    clearAccommodations: (state) => {
      state.accommodations = []
    },
    clearAccommodationInfo: (state) => {
      state.singleAccommodationInfo = null
    },
    clearAccommodationUsers: (state) => {
      state.accomodationUsers = {
        data: [],
        totalCount: 0,
        totalPages: 0,
      }
    },
    clearBeds: (state) => {
      state.beds = {
        data: [],
        totalCount: 0,
        totalPages: 0,
      }
    },
    clearBedsHistory: (state) => {
      state.bedsHistory = initialState.bedsHistory
    },
  },

  extraReducers: {
    [getAccommodationGuests.pending.type]: (state) => {
      state.isLoading = true
    },
    [getAccommodationGuests.fulfilled.type]: (
      state: IAccommodationSlice,
      {
        payload,
      }: {
        payload: IAccommodationGuestsPaginationResponse<IGetAllAccommodationGuestsSingleResponse>
      },
    ) => {
      state.guests = payload
      state.isLoading = false
    },
    [getAccommodationGuests.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getAccommodations.pending.type]: (state) => {
      state.isLoading = true
    },
    [getAccommodations.fulfilled.type]: (
      state: IAccommodationSlice,
      { payload }: { payload: IPaginatedResponse<IGetAllAccommodationsSingleResponse> },
    ) => {
      state.accommodations = payload.data
      state.totalCount = payload.totalCount
      state.totalPages = payload.totalPages
      state.isLoading = false
    },
    [getAccommodations.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [extendStay.pending.type]: (state) => {
      state.isLoading = true
    },
    [extendStay.fulfilled.type]: (state: IAccommodationSlice) => {
      state.isLoading = false
      toast.success('Stay extended successfully')
    },
    [extendStay.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getAccommodationStatistics.rejected.type]: (state) => {
      state.isLoading = false
    },
    [getAccommodationStatistics.pending.type]: (state) => {
      state.isLoading = true
    },
    [getAccommodationStatistics.fulfilled.type]: (
      state: IAccommodationSlice,
      { payload }: { payload: IGetAccommodationStatistics },
    ) => {
      state.statistics = payload
      state.isLoading = false
    },
    [getAccommodations.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getCamps.pending.type]: (state) => {
      state.isLoading = true
    },
    [getCamps.fulfilled.type]: (
      state: IAccommodationSlice,
      { payload }: { payload: IPaginatedResponse<IGetAllCampsSingleResponse> },
    ) => {
      state.camps = payload
      if (!payload.data?.length) {
        state.bedsHistory = initialState.bedsHistory
      }
      state.isLoading = false
    },
    [getCamps.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getCamps.pending.type]: (state) => {
      state.isLoading = true
    },
    [getBeds.pending.type]: (state) => {
      state.isLoading = true
    },
    [getBeds.fulfilled.type]: (
      state: IAccommodationSlice,
      {
        payload,
      }: {
        payload: IPaginatedResponse<IGetAllBedsSingleResponse> & { page?: number }
      },
    ) => {
      const page = payload.page ?? 1
      if (page === 1) {
        state.beds = {
          data: payload.data,
          totalCount: payload.totalCount,
          totalPages: payload.totalPages,
        }
      } else {
        state.beds.data = [...state.beds.data, ...payload.data]
        state.beds.totalCount = payload.totalCount
        state.beds.totalPages = payload.totalPages
      }
      state.isLoading = false
    },
    [getBeds.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getAccommodationUsers.pending.type]: (state) => {
      state.isLoading = true
    },
    [getAccommodationUsers.fulfilled.type]: (
      state: IAccommodationSlice,
      { payload }: { payload: IPaginatedResponse<IGetAllAccommodationUsersSingleResponse> },
    ) => {
      state.accomodationUsers = payload
      state.isLoading = false
    },
    [getAccommodationUsers.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getBedsHistory.pending.type]: (state) => {
      state.isLoading = true
    },
    [getBedsHistory.fulfilled.type]: (
      state: IAccommodationSlice,
      { payload }: { payload: IBedHistoryPaginationResponse<IGetAllBedsHiistorySingleResponse> },
    ) => {
      state.bedsHistory = payload
      state.isLoading = false
    },
    [getBedsHistory.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [checkIn.pending.type]: (state) => {
      state.isLoading = true
    },
    [checkIn.fulfilled.type]: (state: IAccommodationSlice) => {
      toast.success('Check-in successful')
      state.isLoading = false
    },
    [checkIn.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [checkOut.pending.type]: (state) => {
      state.isLoading = true
    },
    [checkOut.fulfilled.type]: (state: IAccommodationSlice) => {
      toast.success('Check-out successful')
      state.isLoading = false
    },
    [checkOut.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getSingleAccommodationInfo.pending.type]: (state) => {
      state.isLoading = true
    },
    [getSingleAccommodationInfo.rejected.type]: (state) => {
      state.isLoading = false
    },
    [getSingleAccommodationInfo.fulfilled.type]: (
      state: IAccommodationSlice,
      { payload }: { payload: IAccommodationInfoResponse },
    ) => {
      state.singleAccommodationInfo = payload
      state.isLoading = false
    },
    [getCheckouts.pending.type]: (state) => {
      state.isLoading = true
    },
    [getCheckouts.fulfilled.type]: (
      state: IAccommodationSlice,
      { payload }: { payload: IPaginatedResponse<IGetAllExpectedCheckoutsSingleResponse> },
    ) => {
      state.checkOuts = payload
      state.isLoading = false
    },
    [getCheckouts.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [createCamp.pending.type]: (state) => {
      state.isLoading = true
    },
    [createCamp.fulfilled.type]: (state) => {
      state.isLoading = false
    },
    [createCamp.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [editAccommodation.pending.type]: (state) => {
      state.isLoading = true
    },
    [editAccommodation.fulfilled.type]: () => {
      toast.success('Accommodation updated successfully')
    },
    [editAccommodation.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [deleteAccommodation.pending.type]: (state) => {
      state.isLoading = true
    },
    [deleteAccommodation.fulfilled.type]: (state: IAccommodationSlice) => {
      toast.success('Accommodation deleted successfully')
      state.isLoading = false
    },
    [deleteAccommodation.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getSingleAccommodation.pending.type]: (state) => {
      state.isLoading = true
    },
    [getSingleAccommodation.fulfilled.type]: (
      state: IAccommodationSlice,
      { payload }: { payload },
    ) => {
      state.isLoading = false
    },
    [getSingleAccommodation.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [markBedClean.pending.type]: (state) => {
      state.isLoading = true
    },
    [markBedClean.fulfilled.type]: (state: IAccommodationSlice) => {
      state.isLoading = false
      toast.success('Bed marked as clean')
    },
    [markBedClean.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [markRoomClean.pending.type]: (state) => {
      state.isLoading = true
    },
    [markRoomClean.fulfilled.type]: (state: IAccommodationSlice) => {
      state.isLoading = false
      toast.success('Room marked as clean')
    },
    [markRoomClean.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [blockRoom.pending.type]: (state) => {
      state.isLoading = true
    },
    [blockRoom.fulfilled.type]: (state: IAccommodationSlice) => {
      state.isLoading = false
      toast.success('Room blocked')
    },
    [blockRoom.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
  },
})

export const {
  clearAccommodations,
  clearAccommodationInfo,
  clearAccommodationUsers,
  clearBeds,
  clearBedsHistory,
} = accommodationSlice.actions
export default accommodationSlice.reducer
